import { Box, IconButton, Typography, useMediaQuery, Button } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import CancelIcon from '@material-ui/icons/Cancel';
import EditIcon from '@material-ui/icons/Edit';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useCurrentUser } from '../../../../Session';
import { UserType } from '../../../../api';
import { useAlert } from '../../../../components/AlertProvider';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import MobileAppBar from '../../../../components/MobileAppBar';
import useCommonStyles from '../../../../useCommonStyles';

type ViewBiddingSessionDetailHeaderProps = {
    title: string;
    goToEdition: () => void;
    cancelBidding: () => Promise<void>;
    downloadPdf?: () => void;
    downloadExcel?: () => void;
    displayOptions?: { disabledCancelBtn: boolean };
};

const ViewBiddingSessionDetailHeader = ({
    cancelBidding,
    title,
    goToEdition,
    downloadPdf,
    downloadExcel,
    displayOptions,
}: ViewBiddingSessionDetailHeaderProps) => {
    const { t } = useTranslation(['biddingSessionsPage', 'common']);
    const commonStyles = useCommonStyles();
    const history = useHistory();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const currentUser = useCurrentUser();
    const { show } = useAlert();

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const confirmPopup = (
        <ConfirmDialog
            body={t('biddingSessionsPage:updateForCancelDialog.body')}
            setContinueConfirmDialog={async () => {
                await cancelBidding();
                show('success', t('biddingSessionsPage:successfulMessage.updateToCancel'));
                setShowConfirmDialog(false);
            }}
            setHideConfirmDialog={() => setShowConfirmDialog(false)}
            show={showConfirmDialog}
            title={t('biddingSessionsPage:updateForCancelDialog.title')}
        />
    );

    if (isSmall) {
        return (
            <MobileAppBar>
                <IconButton color="secondary" onClick={history.goBack}>
                    <ArrowBackIosIcon className={commonStyles.bigFontSize} />
                </IconButton>
                <Box display="flex" justifyContent="center" width="100%">
                    <Typography className={commonStyles.fontWeightBold} color="secondary">
                        {title}
                    </Typography>
                </Box>
                <Box display="flex">
                    {currentUser.type !== UserType.SaleConsultant && currentUser.type !== UserType.Dealer && (
                        <IconButton color="secondary" onClick={downloadPdf}>
                            <GetAppIcon />
                        </IconButton>
                    )}
                    <IconButton color="secondary" onClick={goToEdition}>
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        color="secondary"
                        disabled={displayOptions?.disabledCancelBtn}
                        onClick={() => setShowConfirmDialog(true)}
                    >
                        <CancelIcon />
                    </IconButton>

                    {confirmPopup}
                </Box>
            </MobileAppBar>
        );
    }

    return (
        <Box alignItems="center" className={commonStyles.title} display="flex">
            <Typography>{title}</Typography>
            <Box ml="auto">
                <Box className={commonStyles.buttonGroup}>
                    {currentUser.type !== UserType.SaleConsultant && currentUser.type !== UserType.Dealer && (
                        <>
                            <Button color="secondary" onClick={downloadExcel} size="large" variant="contained">
                                <GetAppIcon fontSize="small" /> {t('common:exportInExcel')}
                            </Button>
                            <Button color="secondary" onClick={downloadPdf} size="large" variant="contained">
                                <GetAppIcon fontSize="small" /> {t('common:exportInPdf')}
                            </Button>
                        </>
                    )}
                    <Button color="secondary" onClick={goToEdition} size="large" variant="contained">
                        {t('common:edit')}
                    </Button>
                    <Button
                        color="secondary"
                        disabled={displayOptions?.disabledCancelBtn}
                        onClick={() => setShowConfirmDialog(true)}
                        size="large"
                        variant="contained"
                    >
                        {t('common:cancel')}
                    </Button>

                    {confirmPopup}
                </Box>
            </Box>
        </Box>
    );
};

export default ViewBiddingSessionDetailHeader;
